import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';
import toast from 'react-hot-toast';
import { sanitizeGame } from './game.slice';

const initialState = {
  library: [],
  cart: [],
  wishlist: [],
  wishlistDetailed: [],
  notifiedGames: [],
  recentlyViewed: [],
  recentlyViewedDetailed: [],
  isCartOpen: false,
  isWishlistOpen: false,
  lastUserPayload: null,
  lastFetched: null,
  loading: false,
  error: null,
};

// Async Thunk: Fetch User Profile Data (including cart and wishlist) with Caching
export const fetchUserData = createAsyncThunk(
  'userSlice/fetchUserData',
  async (userId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/users/${userId}`);
      if (res.data.success && res.data.user) {
        return {
          user: res.data.user,
          userId
        };
      }
      return rejectWithValue('User not found');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
  {
    condition: (userId, { getState }) => {
      const { userSlice } = getState();
      if (userSlice.loading) return false;
      const STALE_TIME = 60 * 1000; // 60 seconds stale-time TTL
      if (
        userSlice.lastUserPayload === userId &&
        userSlice.lastFetched &&
        (Date.now() - userSlice.lastFetched) < STALE_TIME
      ) {
        return false;
      }
      return true;
    }
  }
);

export const fetchUserLibrary = createAsyncThunk(
  'userSlice/fetchUserLibrary',
  async (userId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/users/${userId}/library`);
      if (res.data.success) {
        return res.data.library || [];
      }
      return rejectWithValue('Failed to fetch library');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Add to Cart with DB Sync (unique per game+platform)
export const addToCart = createAsyncThunk(
  'userSlice/addToCart',
  async (game, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;
      const { cart } = state.userSlice;

      const targetId = game._id || game.id;
      const platform = game.selectedPlatform || 'windows';

      // Check if this game+platform combo already exists
      const existing = cart.find(
        item => (item.game._id || item.game.id) === targetId && item.platform === platform
      );

      if (existing) {
        toast.error(`${game.title} (${platform}) is already in your cart!`);
        return cart; // Return unchanged cart
      }

      const updatedCart = [...cart, {
        game: sanitizeGame({ ...game, id: targetId }),
        platform,
        quantity: 1,
        price: game.price,
        originalPrice: game.originalPrice,
        discount: game.discount
      }];

      // Sync with MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        await axiosInstance.patch(`/users/${userId}/cart`, { cart: updatedCart });
      }

      toast.success(`${game.title} added to cart!`);
      return updatedCart;
    } catch (err) {
      console.error('Error adding to cart:', err);
      return rejectWithValue(err.message);
    }
  }
);


// Async Thunk: Remove from Cart with DB Sync
export const removeFromCart = createAsyncThunk(
  'userSlice/removeFromCart',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;
      const { cart } = state.userSlice;

      let targetId;
      let platform;
      if (payload && typeof payload === 'object') {
        targetId = payload.gameId?._id || payload.gameId?.id || payload.gameId || payload.id || payload._id;
        platform = payload.platform;
      } else {
        targetId = payload;
      }

      const item = cart.find(i =>
        (i.game._id || i.game.id) === targetId &&
        (!platform || i.platform === platform)
      );

      const updatedCart = cart.filter(i =>
        !((i.game._id || i.game.id) === targetId && (!platform || i.platform === platform))
      );

      // Sync with MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        await axiosInstance.patch(`/users/${userId}/cart`, { cart: updatedCart });
      }

      if (item?.game?.title) {
        toast.success(`${item.game.title}${platform ? ` (${platform})` : ''} removed from cart`);
      } else {
        toast.success(`Item removed from cart`);
      }

      return updatedCart;
    } catch (err) {
      console.error("Error removing from cart:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Update Quantity with DB Sync
export const updateQuantity = createAsyncThunk(
  'userSlice/updateQuantity',
  async ({ gameId, quantity }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (quantity <= 0) {
        const actionResult = await dispatch(removeFromCart(gameId));
        return actionResult.payload;
      }

      const state = getState();
      const { isAuthenticated, user } = state.auth;
      const { cart } = state.userSlice;

      const updatedCart = cart.map(item =>
        (item.game._id || item.game.id) === gameId
          ? { ...item, quantity }
          : item
      );

      // Sync with MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        await axiosInstance.patch(`/users/${userId}/cart`, { cart: updatedCart });
      }

      return updatedCart;
    } catch (err) {
      console.error("Error updating quantity:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Toggle Wishlist with DB Sync
export const toggleWishlist = createAsyncThunk(
  'userSlice/toggleWishlist',
  async (gameId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;
      const { wishlist } = state.userSlice;
      const { games } = state.game;

      const exists = wishlist.includes(gameId);
      const updatedWishlist = exists
        ? wishlist.filter(id => id !== gameId)
        : [...wishlist, gameId];

      let dbWishlist = null;
      // Sync with MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        const res = await axiosInstance.patch(`/users/${userId}/wishlist`, { wishlist: updatedWishlist });
        if (res.data.success && res.data.wishlist) {
          dbWishlist = res.data.wishlist;
        }
      }

      const gameObj = games.find(g => (g._id || g.id) === gameId);
      const title = gameObj ? gameObj.title : "Game";
      if (exists) {
        toast.success(`${title} removed from wishlist`);
      } else {
        toast.success(`${title} added to wishlist!`);
      }

      return {
        wishlist: updatedWishlist,
        wishlistDetailed: dbWishlist,
        toggledGame: gameObj ? sanitizeGame({ ...gameObj, id: gameObj._id || gameObj.id }) : null,
        exists,
        gameId
      };
    } catch (err) {
      console.error("Error toggling wishlist:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Toggle Upcoming Release Notifications with DB Sync
export const toggleUpcomingNotification = createAsyncThunk(
  'userSlice/toggleUpcomingNotification',
  async ({ gameId, gameTitle }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;
      const { notifiedGames } = state.userSlice;

      const exists = notifiedGames.includes(gameId);
      const updatedNotified = exists
        ? notifiedGames.filter(id => id !== gameId)
        : [...notifiedGames, gameId];

      // Sync with MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        await axiosInstance.patch(`/users/${userId}/upcoming-notifications`, { notifiedGames: updatedNotified });
      }

      if (exists) {
        toast.success(`You will no longer receive notifications for ${gameTitle || 'this game'}.`, {
          style: {
            background: '#070710',
            color: '#ff007a',
            border: '1px solid rgba(255, 0, 122, 0.2)',
            fontSize: '11px',
          }
        });
      } else {
        toast.success(`Notification set! We'll alert you when ${gameTitle || 'this game'} launches.`, {
          style: {
            background: '#070710',
            color: '#00f0ff',
            border: '1px solid rgba(0, 240, 255, 0.2)',
            fontSize: '11px',
          }
        });
      }

      return updatedNotified;
    } catch (err) {
      console.error("Error toggling upcoming notification:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Clear Cart with DB Sync
export const clearCart = createAsyncThunk(
  'userSlice/clearCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;

      // Sync with MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        await axiosInstance.patch(`/users/${userId}/cart`, { cart: [] });
      }

      return [];
    } catch (err) {
      console.error("Error clearing cart:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Sync Recently Viewed history with DB
export const syncRecentlyViewed = createAsyncThunk(
  'userSlice/syncRecentlyViewed',
  async (recentlyViewedIds, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;
      let dbRecent = null;

      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        const res = await axiosInstance.patch(`/users/${userId}/recently-viewed`, { recentlyViewed: recentlyViewedIds });
        if (res.data.success && res.data.recentlyViewed) {
          dbRecent = res.data.recentlyViewed;
        }
      }

      return {
        recentlyViewed: recentlyViewedIds,
        recentlyViewedDetailed: dbRecent
      };
    } catch (err) {
      console.error("Error syncing recently viewed:", err);
      return rejectWithValue(err.message);
    }
  }
);

export const userSlice = createSlice({
  name: 'userSlice',
  initialState,
  reducers: {
    setIsCartOpen: (state, action) => {
      state.isCartOpen = action.payload;
      if (action.payload) state.isWishlistOpen = false;
    },
    setIsWishlistOpen: (state, action) => {
      state.isWishlistOpen = action.payload;
      if (action.payload) state.isCartOpen = false;
    },
    toggleCart: (state) => {
      state.isCartOpen = !state.isCartOpen;
      if (state.isCartOpen) state.isWishlistOpen = false;
    },
    toggleWishlistDrawer: (state) => {
      state.isWishlistOpen = !state.isWishlistOpen;
      if (state.isWishlistOpen) state.isCartOpen = false;
    },
    setLibrary: (state, action) => {
      state.library = action.payload;
    },
    resetUserSessionData: (state) => {
      state.cart = [];
      state.wishlist = [];
      state.wishlistDetailed = [];
      state.library = [];
      state.notifiedGames = [];
      state.recentlyViewed = [];
      state.recentlyViewedDetailed = [];
      state.lastUserPayload = null;
      state.lastFetched = null;
      try {
        localStorage.removeItem('cubegames_recent');
      } catch (e) {
        console.error('localStorage access failed:', e);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserData
      .addCase(fetchUserData.fulfilled, (state, action) => {
        const dbUser = action.payload.user;
        state.lastUserPayload = action.payload.userId;
        state.lastFetched = Date.now();

        if (dbUser.cart) {
          state.cart = dbUser.cart.map(item => ({
            game: sanitizeGame({
              ...item.game,
              id: item.game?._id || item.game?.id
            }),
            platform: item.platform || 'windows',
            quantity: 1,
            price: item.price,
            originalPrice: item.originalPrice,
            discount: item.discount
          })).filter(item => item.game && item.game.id);
        }

        if (dbUser.wishlist) {
          state.wishlist = dbUser.wishlist.map(g => g._id || g.id || g);
          state.wishlistDetailed = dbUser.wishlist.map(g => sanitizeGame({
            ...g,
            id: g._id || g.id
          })).filter(g => g && g.id);
        }

        if (dbUser.library) {
          state.library = dbUser.library.map(g => g._id || g.id || g);
        }

        if (dbUser.notifiedGames) {
          state.notifiedGames = dbUser.notifiedGames.map(g => g._id || g.id || g);
        }

        if (dbUser.recentlyViewed) {
          state.recentlyViewed = dbUser.recentlyViewed.map(g => g._id || g.id || g);
          state.recentlyViewedDetailed = dbUser.recentlyViewed.map(g => sanitizeGame({
            ...g,
            id: g._id || g.id
          })).filter(g => g && g.id);
        }
      })

      // addToCart
      .addCase(addToCart.fulfilled, (state, action) => {
        state.cart = action.payload;
      })

      // removeFromCart
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.cart = action.payload;
      })

      // updateQuantity
      .addCase(updateQuantity.fulfilled, (state, action) => {
        state.cart = action.payload;
      })

      // toggleWishlist
      .addCase(toggleWishlist.fulfilled, (state, action) => {
        state.wishlist = action.payload.wishlist;
        if (action.payload.wishlistDetailed) {
          state.wishlistDetailed = action.payload.wishlistDetailed.map(g => sanitizeGame({
            ...g,
            id: g._id || g.id
          })).filter(g => g && g.id);
        } else {
          // Guest mode logic
          if (action.payload.exists) {
            // Remove
            state.wishlistDetailed = state.wishlistDetailed.filter(g => (g.id || g._id) !== action.payload.gameId);
          } else if (action.payload.toggledGame) {
            // Add
            state.wishlistDetailed = [...state.wishlistDetailed, action.payload.toggledGame];
          }
        }
      })

      // toggleUpcomingNotification
      .addCase(toggleUpcomingNotification.fulfilled, (state, action) => {
        state.notifiedGames = action.payload;
      })

      // clearCart
      .addCase(clearCart.fulfilled, (state, action) => {
        state.cart = action.payload;
      })

      .addCase(fetchUserLibrary.fulfilled, (state, action) => {
        state.library = (action.payload || []).map((g) => g._id || g.id || g);
      })
      
      .addCase(syncRecentlyViewed.fulfilled, (state, action) => {
        state.recentlyViewed = action.payload.recentlyViewed;
        if (action.payload.recentlyViewedDetailed) {
          state.recentlyViewedDetailed = action.payload.recentlyViewedDetailed.map(g => sanitizeGame({
            ...g,
            id: g._id || g.id
          })).filter(g => g && g.id);
        }
      });
  }
});

export const {
  setIsCartOpen,
  setIsWishlistOpen,
  toggleCart,
  toggleWishlistDrawer,
  setLibrary,
  resetUserSessionData
} = userSlice.actions;

export default userSlice.reducer;
